import { useState } from "react";
import FilesContent from "./FilesContent";
import {
  ActionIcon,
  Anchor,
  Button,
  Flex,
  Group,
  Indicator,
  Modal,
  Text,
  Tooltip,
} from "@mantine/core";
import { useListingLoader } from "../../../components/hooks/ListingHooks";
import { editFile, getFiles } from "../../../api/FIleAPI";
import { IconFile, IconMicrophone, IconPaperclip, IconPhoto } from "@tabler/icons-react";
import { useLanguage } from "../../../stores/LanguageStore";
import BackButton from "../../../components/View/BackButton";
import TableLayoutBlock from "../../../layouts/Table/TableLayoutBlock";

const imageDef = ["jpg", "jpeg", "png", "gif"];
const codeDef = ["html"];
const powerpointDef = ["pot", "potx", "ppt", "pptx"];
const excelDef = ["xls", "xlsx", "csv"];
const docsDef = ["pdf", "doc", "docx"];
const audioDef = ["mp3", "wav"];

const FilesModal = ({
  isTemplate,
  editorRef,
  isCode,
  buttonProps,
  isLongButton,
  handleFileAdd,
  isLinkedin,
  defaultType,
  isNew,
  label,
  audioOnly
}) => {
  const lang = useLanguage((s) => s.language);
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [folder, setFolder] = useState(null);

  const handleOpen = () => {
    setOpened(true);
    setLoading(true);
  };

  const handleClose = () => {
    setOpened(false);
  };

  const handleInsertContent = (content) => {
    if (isCode) {
      editorRef.current.editor.session.insert(
        editorRef.current.editor.getCursorPosition(),
        content
      );
      return handleClose();
    }
    editorRef.current.execCommand("mceInsertContent", false, content);
    handleClose();
  };

  const onFileAdd = isLinkedin ? (content) => {
    handleClose()
    return handleFileAdd(content)
  } : undefined

  useListingLoader({
    loading,
    setLoading,
    listGetter: getFiles,
    responseField: "files_list",
    getRows: (result) => {
      const filesWithType = result.map((file) => {
        const fileExtension = file.file_url;
        let type;
        if (imageDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "image";
        }
        if (codeDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "code";
        }
        if (powerpointDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "powerpoint";
        }
        if (excelDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "excel";
        }
        if (docsDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "docs";
        }
        if (audioDef.some((ext) => fileExtension.endsWith(ext))) {
          type = "audio";
        }
        return { ...file, type };
      });

      return filesWithType;
    },
    setRows,
    folderId: folder?.id,
  });

  return (
    <>
      {isLongButton ?
        isNew ? (
          <Indicator color="red" offset={4} position="top-start" size={22} label={"new"} styles={{ indicator: { fontSize: 8 } }} h={"100%"} >
            <Button
              leftSection={isLinkedin ? defaultType === "audio" ? <IconMicrophone size={18} /> : <IconPaperclip size={18} /> : <IconPhoto size={18} />}
              onClick={handleOpen}
              color="primary"
              size="sm"
              variant={"light"}
              h={32}
              {...buttonProps}
            >
              {isLinkedin ? defaultType === "audio" ? lang.files.grid.import.button_label_audio : lang.files.grid.import.button_label_file : lang.files.grid.import.button_tooltip}
            </Button>
          </Indicator>
        ) : (
          <Button
            leftSection={isLinkedin ? defaultType === "audio" ? <IconMicrophone size={18} /> : <IconPaperclip size={18} /> : <IconPhoto size={18} />}
            onClick={handleOpen}
            color="primary"
            size="sm"
            variant={"light"}
            h={32}
            {...buttonProps}
          >
            {label ? label : isLinkedin ? defaultType === "audio" ? lang.files.grid.import.button_label_audio : lang.files.grid.import.button_label_file : lang.files.grid.import.button_tooltip}
          </Button>
        ) : (
          <Tooltip label={lang.files.grid.import.button_tooltip}>
            <ActionIcon
              onClick={handleOpen}
              color="primary"
              variant="light"
              className="to-blue"
              radius={"md"}
              {...buttonProps}
            >
              <IconPhoto size={18} />
            </ActionIcon>
          </Tooltip>
        )}

      {opened && (
        <Modal
          opened={opened}
          onClose={handleClose}
          title={
            <Text size="lg">
              {isTemplate
                ? lang.files.grid.import.dialog.template_title
                : lang.files.grid.import.dialog.title}
              <Anchor
                href="/settings/tools/files"
                target="_blank"
                title={lang.files.grid.import.dialog.goto_files}
              >
                {" "}
                {lang.files.grid.import.dialog.your_files}
              </Anchor>{" "}
              {lang.files.grid.import.dialog.uploaded}
            </Text>
          }
          size={"100%"}
        >
          <Group
            w={"100%"}
            h={"70vh"}
            style={{ overflow: "auto" }}
            align="start"
          >
            <TableLayoutBlock
              folderType={"File"}
              parentRoute={"/settings/tools/files"}
              editFunction={editFile}
              setTableLoading={setLoading}
              wrapperProps={{
                h: "100%",
                mx: 0,
                mt: 0
              }}
              folderId={folder?.id || null}
              onFolderItemClick={(item) => {
                setFolder(item || null);
                setLoading(true);
              }}
            >
              <Flex h={"100%"} p={"sm"} w={"100%"}>
                <FilesContent
                  loading={loading}
                  files={rows}
                  setLoading={setLoading}
                  handleInsertContent={handleInsertContent}
                  onFileAdd={onFileAdd}
                  isTemplate={isTemplate}
                  handleClose={handleClose}
                  setRows={setRows}
                  isCode={isCode}
                  folder={folder}
                  defaultType={defaultType}
                  inModal
                  audioOnly={audioOnly}
                />
              </Flex>
            </TableLayoutBlock>
          </Group>
          <Group justify="end" mt={"md"}>
            <BackButton onClick={handleClose} size='md' />
          </Group>
        </Modal>
      )}
    </>
  );
};

export default FilesModal;
